import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem,
  Select, Switch, TextField, Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SaveIcon from '@mui/icons-material/Save'
import { FC, useEffect, useMemo, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { getJson, getUrl, putJson } from '../providers/data'
import { clearSettingsCache, OrganizationSettings } from '../utils/useOrganizationSettings'
import { OrganizationSettingsUpdate, SyncStatusOut } from '../types/apiRequests'

const Settings: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const [settings, setSettings] = useState<OrganizationSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const [typeConfig, setTypeConfig] = useState<Record<string, string>>({})
  const [orgName, setOrgName] = useState('')
  const [orgOin, setOrgOin] = useState('')
  const [orgType, setOrgType] = useState('')

  const schema = settings?.type_config_schema ?? []
  const hasTypeConfig = schema.length > 0

  const urlFields = useMemo(() => new Set(schema.filter((f) => f.field_type === 'url').map((f) => f.key)), [schema])
  // eslint-disable-next-line security/detect-object-injection
  const hasTrailingSlashError = (key: string) => urlFields.has(key) && typeConfig[key]?.endsWith('/')

  const applySettings = (data: OrganizationSettings) => {
    setSettings(data)
    setOrgName(data.name ?? '')
    setOrgOin(data.oin_number ?? '')
    setOrgType(data.organization_type ?? '')
    const config: Record<string, string> = {}
    for (const field of data.type_config_schema ?? []) {
      config[field.key] = (data.type_config?.[field.key]) ?? ''
    }
    setTypeConfig(config)
  }

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getJson<OrganizationSettings>(`${getUrl()}/organization_settings`)
        applySettings(data)
      } catch {
        notify('ra.notification.http_error', { type: 'error' })
      } finally {
        setLoading(false)
      }
    }
    void fetchSettings()
  }, [notify])

  const handleSave = async () => {
    setSaving(true)
    try {
      const typeConfigPayload: Record<string, string | null> = {}
      for (const [key, value] of Object.entries(typeConfig)) {
        // eslint-disable-next-line security/detect-object-injection
        typeConfigPayload[key] = value || null
      }
      // API field names — eslint-disable-next-line camelcase
      const body: OrganizationSettingsUpdate = {
        name: orgName,
        oin_number: orgOin || null, // eslint-disable-line camelcase
        organization_type: orgType, // eslint-disable-line camelcase
        type_config: typeConfigPayload, // eslint-disable-line camelcase
      }
      const data = await putJson<OrganizationSettings>(`${getUrl()}/organization_settings`, body)
      applySettings(data)
      clearSettingsCache()
      notify(translate('settings.saved'), { type: 'success' })
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleSyncToggle = async () => {
    setConfirmOpen(false)
    try {
      const result = await putJson<SyncStatusOut>(
        `${getUrl()}/notification_configuration_sync_status`,
        { sync_enabled: !settings?.sync_enabled }, // eslint-disable-line camelcase
      )
      // eslint-disable-next-line camelcase
      setSettings((prev) => prev ? { ...prev, sync_enabled: result.sync_enabled } : prev)
      clearSettingsCache()
      notify(
        result.sync_enabled ? translate('data.syncEnabled') : translate('data.syncDisabled'),
        { type: 'success' },
      )
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    }
  }

  if (loading) return null

  return (
    <Box sx={{ maxWidth: 600, mt: 2, ml: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('settings.title')}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('settings.organization')}
          </Typography>
          <TextField
            fullWidth
            label={translate('settings.organizationName')}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={translate('settings.organizationAuthId')}
            value={settings?.organization_id ?? ''}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={translate('settings.organizationOin')}
            value={orgOin}
            onChange={(e) => setOrgOin(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{translate('settings.organizationType')}</InputLabel>
            <Select
              value={orgType}
              label={translate('settings.organizationType')}
              onChange={(e) => setOrgType(e.target.value)}
            >
              <MenuItem value="zaken">Zaken</MenuItem>
              <MenuItem value="overige">Overige</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {translate('settings.notificationSync')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('settings.notificationSyncDescription')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={settings?.sync_enabled ?? false}
              onChange={() => setConfirmOpen(true)}
              color="primary"
            />
            <Typography variant="body2" color="text.secondary">
              {settings?.sync_enabled ? translate('data.syncEnabled') : translate('data.syncDisabled')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {translate('settings.notificationSyncHelper')}
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {settings?.sync_enabled ? translate('data.confirmDisableSync') : translate('data.confirmEnableSync')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {settings?.sync_enabled ? translate('data.confirmDisableSyncText') : translate('data.confirmEnableSyncText')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{translate('ra.action.cancel')}</Button>
          <Button onClick={() => void handleSyncToggle()} color="primary" variant="contained" autoFocus>
            {translate('ra.action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {hasTypeConfig && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('settings.typeConfig')}
          </Typography>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              {schema.map((field) => {
                const isApiKeyOnly = field.key === 'zs_user_interface' || field.key === 'zs_user_override'
                if (isApiKeyOnly && typeConfig.zaaksysteem_auth_type !== 'api_key') return null

                const label = translate(`settings.${field.label}`, { _: field.label })

                if (field.field_type.startsWith('select:')) {
                  const options = field.field_type.replace('select:', '').split(',')
                  // eslint-disable-next-line camelcase
                  const prefixMap: Record<string, string> = {
                    zaaksysteem_auth_type: 'settings.authType_', // eslint-disable-line camelcase
                    zs_user_interface: 'settings.userInterface_', // eslint-disable-line camelcase
                  }
                  // eslint-disable-next-line security/detect-object-injection
                  const prefix = prefixMap[field.key] ?? ''
                  return (
                    <FormControl key={field.key} fullWidth sx={{ mb: 2 }}>
                      <InputLabel>{label}</InputLabel>
                      <Select
                        value={typeConfig[field.key] ?? ''}
                        label={label}
                        onChange={(e) => setTypeConfig((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {options.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {prefix ? translate(`${prefix}${opt}`, { _: opt }) : opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                }

                return (
                  <TextField
                    key={field.key}
                    fullWidth
                    label={label}
                    type={field.field_type === 'password' ? 'password' : 'text'}
                    value={typeConfig[field.key] ?? ''}
                    onChange={(e) => setTypeConfig((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    error={hasTrailingSlashError(field.key)}
                    helperText={hasTrailingSlashError(field.key) ? translate('settings.urlNoTrailingSlash') : undefined}
                    sx={{ mb: 2 }}
                  />
                )
              })}
            </CardContent>
          </Card>
        </>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {translate('verzoekenoverzicht.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('verzoekenoverzicht.settingsDescription')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => void navigate('/verzoekenoverzicht')}
          >
            {translate('verzoekenoverzicht.openOverview')}
          </Button>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={() => void handleSave()}
        disabled={saving || Array.from(urlFields).some((key) => hasTrailingSlashError(key))}
        sx={{ mb: 2 }}
      >
        {translate('ra.action.save')}
      </Button>
    </Box>
  )
}

export default Settings
